/**
 * Unified analytics tracking utility
 * Sends events to GA4, Ahrefs Web Analytics (custom events), and Sentry
 */

import type { SrpSort } from '../search-router/srp-sort';
import { addBreadcrumb, captureException } from '../sentry/sentry-lazy';
import type { AnalyticsEventParams, TrackingResult, AnalyticsEventAction, AnalyticsEventCategory } from '../../types/analytics';
import type { AnalyticsEvent } from '../../types/analytics-events';
import { sendGA4Event } from './providers/ga4';
import { isAlgoliaSearchQueryId } from '../search-router/is-search-query-id';
import {
  sendAhrefsJobApplyEvents,
  sendAhrefsWebAnalyticsLegacyEvent,
  sendAhrefsWebAnalyticsTypedEvent,
} from './providers/ahrefs-web-analytics';
import { isClientAnalyticsAllowed } from '../cookie-consent/client-analytics-allowed';
import { sendTrackEventBeacon } from './track-event-beacon';
import { queuePreConsentProductEvent } from './pending-product-events';
import { CHOKEPOINT_COVERED_EVENT_NAMES } from './track-event-exclusion-list';

// Declare window.dataLayer for GA4
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Convert AnalyticsEvent to AnalyticsEventParams format for backward compatibility
 */
function convertTypedEventToParams(event: AnalyticsEvent): AnalyticsEventParams {
  const baseParams: AnalyticsEventParams = {
    category: 'interaction' as AnalyticsEventCategory,
    action: event.name as AnalyticsEventAction,
  };

  switch (event.name) {
    case 'job_view':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.job_title;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.is_remote !== undefined) baseParams.is_remote = event.params.is_remote;
      if (event.params.has_salary_insight !== undefined) baseParams.has_salary_insight = event.params.has_salary_insight;
      break;
      
    case 'company_view':
      baseParams.category = 'company' as any;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.company_name || event.params.company_id;
      if (event.params.company_name) baseParams.company_name = event.params.company_name;
      if (event.params.industry) baseParams.industry = event.params.industry;
      break;
      
    case 'srp_view':
      baseParams.category = 'search';
      baseParams.query = event.params.query_text;
      baseParams.hits_count = event.params.result_count;
      baseParams.label = event.params.query_text || 'srp_view';
      if (event.params.location_label) baseParams.location_label = event.params.location_label;
      if (event.params.filters_applied) baseParams.filters_applied = event.params.filters_applied;
      break;
      
    case 'salary_insight_view':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.company_id = event.params.company_id;
      baseParams.label = 'salary_insight_view';
      if (event.params.has_exact_salary !== undefined) baseParams.has_exact_salary = event.params.has_exact_salary;
      if (event.params.has_estimated_salary !== undefined) baseParams.has_estimated_salary = event.params.has_estimated_salary;
      break;
      
    case 'job_click':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.job_title;
      if (event.params.position !== undefined) baseParams.value = event.params.position;
      break;

    case 'salary_insight_interaction':
      baseParams.category = 'job';
      baseParams.label = event.params.interaction_type;
      baseParams.interaction_type = event.params.interaction_type;
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      break;

    case 'job_mark_viewed':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.label = event.params.job_title;
      break;

    case 'job_save':
    case 'job_unsave':
    case 'job_share':
      baseParams.category = 'job';
      baseParams.job_id = event.params.job_id;
      baseParams.job_title = event.params.job_title;
      baseParams.label = event.params.job_title;
      break;
      
    case 'filter_add':
    case 'filter_remove':
      baseParams.category = 'search';
      baseParams.filter_key = event.params.filter_key;
      baseParams.filter_value = event.params.filter_value;
      baseParams.label = `${event.params.filter_key}: ${event.params.filter_value}`;
      break;
      
    case 'sort_change':
      baseParams.category = 'search';
      baseParams.sort_type = event.params.sort_type;
      baseParams.label = event.params.sort_type;
      break;
      
    case 'search_pagination':
      baseParams.category = 'navigation';
      baseParams.page_number = event.params.page_number;
      baseParams.label = `Page ${event.params.page_number}`;
      break;
      
    case 'search_suggestion_click':
      baseParams.category = 'search';
      baseParams.query = event.params.suggestion;
      baseParams.label = event.params.suggestion;
      break;
      
    case 'company_link_click':
      baseParams.category = 'interaction';
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.company_id;
      break;

    case 'company_follow':
    case 'company_unfollow':
      baseParams.category = 'company' as any;
      baseParams.company_id = event.params.company_id;
      baseParams.label = event.params.company_id;
      break;
      
    case 'spa_page_view':
      baseParams.category = 'navigation';
      baseParams.label = event.params.page_type || 'spa_page_view';
      if (event.params.job_id) baseParams.job_id = event.params.job_id;
      if (event.params.company_id) baseParams.company_id = event.params.company_id;
      if (event.params.srp_id) baseParams.srp_id = event.params.srp_id;
      if (event.params.page_type) baseParams.page_type = event.params.page_type;
      break;
      
  }
  
  // Add locale if present
  if (event.params.locale) {
    baseParams.locale = event.params.locale;
  }
  
  return baseParams;
}

/**
 * Track an event across GA4, Ahrefs, and Sentry
 * Supports both typed AnalyticsEvent and legacy AnalyticsEventParams
 */
export function trackEvent(eventOrParams: AnalyticsEvent | AnalyticsEventParams): TrackingResult {
  const result: TrackingResult = {
    ga4: false,
    ahrefs: false,
    sentry: false,
  };

  // Check if this is a typed event or legacy params
  const isTypedEvent = 'name' in eventOrParams && 'params' in eventOrParams;
  
  let params: AnalyticsEventParams;
  let typedEvent: AnalyticsEvent | null = null;
  
  if (isTypedEvent) {
    typedEvent = eventOrParams as AnalyticsEvent;
    params = convertTypedEventToParams(typedEvent);
    
    // Development validation
    if (process.env.NODE_ENV === 'development') {
      // Validate required fields based on event type
      const validationErrors: string[] = [];
      
      switch (typedEvent.name) {
        case 'job_view':
          if (!typedEvent.params.job_id) validationErrors.push('job_id is required');
          if (!typedEvent.params.job_title) validationErrors.push('job_title is required');
          if (!typedEvent.params.company_id) validationErrors.push('company_id is required');
          break;
        case 'company_view':
          if (!typedEvent.params.company_id) validationErrors.push('company_id is required');
          break;
        case 'srp_view':
          if (!typedEvent.params.srp_id) validationErrors.push('srp_id is required');
          break;
        case 'salary_insight_view':
          if (!typedEvent.params.job_id) validationErrors.push('job_id is required');
          if (!typedEvent.params.company_id) validationErrors.push('company_id is required');
          break;
      }
      
      if (validationErrors.length > 0) {
        console.warn('[Analytics] Validation errors for event:', typedEvent.name, validationErrors);
      }
      
      // Log event structure for debugging
      console.log('[Analytics] Tracking typed event:', typedEvent.name, typedEvent.params);
    }
  } else {
    params = eventOrParams as AnalyticsEventParams;
  }

  try {
    const allowThirdPartyAnalytics = isClientAnalyticsAllowed();

    // GA4 tracking - use adapter for typed events, legacy path for old format
    if (isTypedEvent && typedEvent) {
      result.ga4 = sendGA4Event(typedEvent);
      result.ahrefs = sendAhrefsWebAnalyticsTypedEvent(typedEvent);
    } else {
      // Legacy GA4 tracking
      if (allowThirdPartyAnalytics && typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', params.action, {
          event_category: params.category,
          event_label: params.label,
          value: params.value,
          non_interaction: params.non_interaction,
          // Add custom parameters
          ...(params.query && { search_term: params.query }),
          ...(params.filter_key && { filter_key: params.filter_key }),
          ...(params.filter_value && { filter_value: params.filter_value }),
          ...(params.sort_type && { sort_type: params.sort_type }),
          ...(params.page_number && { page_number: params.page_number }),
          ...(params.hits_count && { hits_count: params.hits_count }),
          ...(params.job_id && { job_id: params.job_id }),
          ...(params.company_id && { company_id: params.company_id }),
          ...(params.job_title && { job_title: params.job_title }),
          // NOT `source`: GA4 reserves that key (with medium/campaign/term/content/gclid) for
          // event-based campaign attribution — sending it here silently attributed sessions to
          // e.g. "srp"/"similar_jobs" as if they were traffic sources instead of UI click origin.
          ...(params.source && { click_source: params.source }),
          ...(params.locale && { locale: params.locale }),
          ...(params.url && { url: params.url }),
          // Explicit undefined-check, not truthy: `false` (the guest case, the one we most want
          // to see) must not be dropped the way a falsy `&&`-guard would silently drop it.
          ...(params.logged_in !== undefined && { logged_in: params.logged_in }),
        });
        result.ga4 = true;
      }
      if (allowThirdPartyAnalytics && typeof window !== 'undefined') {
        result.ahrefs = sendAhrefsWebAnalyticsLegacyEvent(params);
      }
    }

    // Permanent product-events store (ProductEventsTable, via /api/track-event): full parity
    // with everything GA4 sees, for both signed-in and anonymous users, EXCEPT event names
    // already recorded through the server-side Discord chokepoint (see
    // track-event-exclusion-list.ts's comment for why each one is or isn't excluded) — those
    // would otherwise be double-written, once from this client call and once from the server
    // route that actually performed the action.
    const productEventName = isTypedEvent && typedEvent ? typedEvent.name : params.action;
    if (productEventName && !CHOKEPOINT_COVERED_EVENT_NAMES.has(productEventName)) {
      const productEventParams = (isTypedEvent && typedEvent ? typedEvent.params : params) as Record<string, unknown>;
      if (allowThirdPartyAnalytics) {
        sendTrackEventBeacon(productEventName, productEventParams);
      } else {
        // No consent choice yet: hold in memory, sent only if the visitor accepts (see pending-product-events.ts).
        queuePreConsentProductEvent(productEventName, productEventParams);
      }
    }

    // Sentry tracking - add breadcrumb for all events
    if (typeof window !== 'undefined') {
      addBreadcrumb({
        category: params.category,
        message: params.action,
        data: {
          label: params.label,
          value: params.value,
          ...params,
          ...(isTypedEvent && typedEvent ? { typed_event_name: typedEvent.name } : {}),
        },
        level: params.category === 'error' ? 'error' : 'info',
      });
      result.sentry = true;
    }
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
    // Report error to Sentry
    captureException(error, {
      tags: { component: 'analytics' },
      extra: { params, isTypedEvent, typedEventName: isTypedEvent && typedEvent ? typedEvent.name : undefined },
    });
  }

  return result;
}

/**
 * Store query ID in session storage for tracking across user actions
 */
function storeQueryID(queryID: string, query: string, hitsCount?: number) {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('current_query_id', queryID);
    sessionStorage.setItem(`query_${queryID}`, JSON.stringify({
      query,
      hitsCount,
      timestamp: Date.now(),
      url: window.location.href,
    }));
  }
}

/**
 * Get the current query ID from session storage
 */
function getCurrentQueryID(): string | null {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem('current_query_id');
  }
  return null;
}

/**
 * Clear the current query ID (called on new search)
 * Export for use in SearchContext
 */
export function clearQueryID() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('current_query_id');
  }
}

/**
 * Store the search queryID for cross-action attribution (e.g. job apply query_id).
 * Deliberately emits NO analytics event: the GA4/Ahrefs `search` event is sent by
 * trackSearchAnalyticsGa4Debounced — a second per-search event here would double-count.
 */
export function trackSearchQuery(params: {
  query: string;
  locale?: string;
  hitsCount?: number;
  queryID?: string; // Algolia queryID from search results
}): void {
  const queryID =
    params.queryID && isAlgoliaSearchQueryId(params.queryID) ? params.queryID.trim() : undefined;
  if (queryID) {
    storeQueryID(queryID, params.query, params.hitsCount);
  }
}

/**
 * Track a filter addition
 */
export function trackFilterAdd(params: {
  filterKey: string;
  filterValue: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'search',
    action: 'filter_add',
    filter_key: params.filterKey,
    filter_value: params.filterValue,
    label: `${params.filterKey}: ${params.filterValue}`,
    locale: params.locale,
  });
}

/**
 * Track a filter removal
 */
export function trackFilterRemove(params: {
  filterKey: string;
  filterValue: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'search',
    action: 'filter_remove',
    filter_key: params.filterKey,
    filter_value: params.filterValue,
    label: `${params.filterKey}: ${params.filterValue}`,
    locale: params.locale,
  });
}

/**
 * Track sort change
 */
export function trackSortChange(params: {
  sortType: SrpSort;
  locale?: string;
}) {
  return trackEvent({
    category: 'search',
    action: 'sort_change',
    sort_type: params.sortType,
    label: params.sortType,
    locale: params.locale,
  });
}

/**
 * Track job click
 */
export function trackJobClick(params: {
  jobId: string;
  jobTitle: string;
  companyId: string;
  position?: number;
  /** Which surface drove the click ('srp' assumed when omitted). */
  source?: 'srp' | 'map' | 'similar_jobs' | 'company_profile';
  locale?: string;
  queryId?: string;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_click',
    job_id: params.jobId,
    job_title: params.jobTitle,
    company_id: params.companyId,
    label: params.jobTitle,
    value: params.position,
    source: params.source,
    locale: params.locale,
  });
}

/**
 * Track job view (detail page view)
 */
export function trackJobView(params: {
  jobId: string;
  jobTitle: string;
  companyId: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_view',
    job_id: params.jobId,
    job_title: params.jobTitle,
    company_id: params.companyId,
    label: params.jobTitle,
    locale: params.locale,
  });
}

/**
 * Track job save
 */
export function trackJobSave(params: {
  jobId: string;
  jobTitle: string;
  locale?: string;
  loggedIn?: boolean;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_save',
    job_id: params.jobId,
    job_title: params.jobTitle,
    label: params.jobTitle,
    locale: params.locale,
    logged_in: params.loggedIn,
  });
}

/**
 * Track job unsave
 */
export function trackJobUnsave(params: {
  jobId: string;
  jobTitle: string;
  locale?: string;
  loggedIn?: boolean;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_unsave',
    job_id: params.jobId,
    job_title: params.jobTitle,
    label: params.jobTitle,
    locale: params.locale,
    logged_in: params.loggedIn,
  });
}

/**
 * Track job share
 */
export function trackJobShare(params: {
  jobId: string;
  jobTitle: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'job',
    action: 'job_share',
    job_id: params.jobId,
    job_title: params.jobTitle,
    label: params.jobTitle,
    locale: params.locale,
  });
}

/**
 * Track company link click
 */
export function trackCompanyLink(params: {
  companyId: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'interaction',
    action: 'company_link_click',
    company_id: params.companyId,
    label: params.companyId,
    locale: params.locale,
  });
}

/**
 * Track pagination
 */
export function trackPagination(params: {
  pageNumber: number;
  locale?: string;
}) {
  return trackEvent({
    category: 'navigation',
    action: 'search_pagination',
    page_number: params.pageNumber,
    label: `Page ${params.pageNumber}`,
    locale: params.locale,
  });
}

/**
 * Track search suggestion click
 */
export function trackSearchSuggestion(params: {
  suggestion: string;
  locale?: string;
}) {
  return trackEvent({
    category: 'search',
    action: 'search_suggestion_click',
    query: params.suggestion,
    label: params.suggestion,
    locale: params.locale,
  });
}

/**
 * Where a job application signal came from. Only `apply_cta` is a
 * marketplace-driven conversion; the others are the user's own bookkeeping
 * in the application tracker and must not inflate the conversion metric.
 */
export type JobApplySource = 'apply_cta' | 'tracker_marked' | 'external_logged';

/**
 * Track job application.
 * `apply_cta` fires `job_apply_conversion` (the GA4 key event);
 * tracker bookkeeping fires `job_application_logged` instead.
 */
export function trackJobApply(params: {
  jobId: string;
  jobTitle: string;
  companyId: string;
  source: JobApplySource;
  position?: number;
  locale?: string;
  method?: 'external_link' | 'email' | 'company_website';
}) {
  const queryID = getCurrentQueryID();
  const eventName = params.source === 'apply_cta' ? 'job_apply_conversion' : 'job_application_logged';

  const eventPayload = {
    category: 'conversion',
    // NOT `source`: GA4 reserves that key (with medium/campaign/term/content/gclid) for
    // event-based campaign attribution — sending it here was silently attributing sessions
    // to "apply_cta" as if it were a traffic source instead of an in-app apply-signal origin.
    apply_source: params.source,
    job_id: params.jobId,
    job_title: params.jobTitle,
    company_id: params.companyId,
    method: params.method || 'external_link',
    value: 1,
    query_id: queryID,
    locale: params.locale,
  };

  let ga4Sent = false;
  if (isClientAnalyticsAllowed() && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventPayload);
    ga4Sent = true;
  }

  const ahrefsApply =
    isClientAnalyticsAllowed() && typeof window !== 'undefined'
      ? sendAhrefsJobApplyEvents({
          eventName,
          jobId: params.jobId,
          jobTitle: params.jobTitle,
          companyId: params.companyId,
          applySource: params.source,
          method: params.method,
          locale: params.locale,
          queryID,
        })
      : false;

  // This function doesn't go through trackEvent() (it builds its own GA4/Ahrefs payload above),
  // so it needs its own product-events beacon call — neither job_apply_conversion nor
  // job_application_logged is in CHOKEPOINT_COVERED_EVENT_NAMES, so no dedup check needed here.
  if (isClientAnalyticsAllowed()) {
    sendTrackEventBeacon(eventName, eventPayload as Record<string, unknown>);
  } else {
    queuePreConsentProductEvent(eventName, eventPayload as Record<string, unknown>);
  }

  // Sentry breadcrumb
  if (typeof window !== 'undefined') {
    addBreadcrumb({
      category: 'conversion',
      message: 'Job Application',
      level: 'info',
      data: {
        job_id: params.jobId,
        job_title: params.jobTitle,
        company_id: params.companyId,
        source: params.source,
        method: params.method,
        query_id: queryID,
      },
    });
  }

  return {
    ga4: ga4Sent,
    ahrefs: ahrefsApply,
    sentry: true,
  };
}



/**
 * Profile redesign analytics (docs/profile-redesign-spec.md, section 1e). Phase 1 only wires up
 * these helpers; Phase 2's profile UI is what actually calls them as sheets/panels are used.
 */

/** Fires when a reducer stamps `sectionUpdatedAt` for a profile section (an autosave landed). */
export function trackProfileSectionSaved(params: { section: string; locale?: string }) {
  return trackEvent({
    category: 'profile',
    action: 'profile_section_saved',
    section: params.section,
    label: params.section,
    locale: params.locale,
  });
}

/** Fires once, the first time the first-visit how-to popover is dismissed. */
export function trackProfileHowtoSeen(params: { locale?: string } = {}) {
  return trackEvent({
    category: 'profile',
    action: 'profile_howto_seen',
    locale: params.locale,
  });
}

/** Fires when the user taps the live match-count hero figure ("See them") to jump to the SRP. */
export function trackProfileMatchCountClick(params: { matchCount?: number; locale?: string }) {
  return trackEvent({
    category: 'profile',
    action: 'profile_match_count_click',
    value: params.matchCount,
    locale: params.locale,
  });
}

/** Fires when the user taps one of the profile page's career-path suggestion chips (TEC-504). */
export function trackCareerPathChipClick(params: { slug: string; jobCount?: number; locale?: string }) {
  return trackEvent({
    category: 'profile',
    action: 'career_path_chip_click',
    label: params.slug,
    value: params.jobCount,
    locale: params.locale,
  });
}

/** Fires when an email alert is created from the matching panel's "Email me new ones" action. */
export function trackProfileAlertFromMatch(params: { matchCount?: number; locale?: string }) {
  return trackEvent({
    category: 'profile',
    action: 'profile_alert_from_match',
    value: params.matchCount,
    locale: params.locale,
  });
}

/** Fires when the user requests (or accepts) a CV translation between locales. */
export function trackProfileCvTranslate(params: { from: string; to: string }) {
  return trackEvent({
    category: 'profile',
    action: 'profile_cv_translate',
    label: `${params.from}->${params.to}`,
    locale: params.to,
  });
}
